import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

const D3BarChart = () => {
  // Define data points and labels
  const data = [
    {
      startDate: "2018-08",
      endDate: "2022-07",
      label: "Machine learning Engineer, Python Developer",
    },
    {
      startDate: "2022-08",
      endDate: "2024-05",
      label: "Full-stack developer, Data Scientist",
    },
    {
      startDate: "2016-01",
      endDate: "2018-04",
      label: "Software engineer in Java",
    },
    // {
    //   startDate: "2011-03",
    //   endDate: "2015-12",
    //   label: "Software engineer in C#",
    // },
  ];

  // Ref for SVG element
  const svgRef = useRef();

  // State to track if mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Parse dates
    const parseDate = d3.timeParse("%Y-%m");

    // Define margin and dimensions
    const margin = { top: 20, right: 20, bottom: 40, left: 50 };
    const width = isMobile
      ? 450 - margin.left - margin.right
      : 800 - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    // Clear previous SVG content
    d3.select(svgRef.current).selectAll("*").remove();

    // Create SVG element
    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    // Define scales and axes
    let xScale, yScale, xAxis, yAxis;
    if (isMobile) {
      // Vertical bars for mobile
      xScale = d3.scaleLinear().domain([0, 1]).range([0, width]);
      yScale = d3
        .scaleTime()
        .domain([
          d3.min(data, (d) => parseDate(d.startDate)),
          d3.max(data, (d) => parseDate(d.endDate)),
        ])
        .range([height, 0]);

      xAxis = d3.axisBottom(xScale).ticks(1); // Only one tick mark
      yAxis = d3.axisLeft(yScale).tickFormat(d3.timeFormat("%Y-%m"));
    } else {
      // Horizontal bars for desktop
      xScale = d3
        .scaleTime()
        .domain([
          d3.min(data, (d) => parseDate(d.startDate)),
          d3.max(data, (d) => parseDate(d.endDate)),
        ])
        .range([0, width]);

      yScale = d3.scaleLinear().domain([0, 1]).range([height, 0]);

      xAxis = d3.axisBottom(xScale).tickFormat(d3.timeFormat("%Y-%m"));
      yAxis = d3.axisLeft(yScale).ticks(1); // Only one tick mark
    }

    // Draw x axis
    svg.append("g").attr("transform", `translate(0, ${height})`).call(xAxis);

    // Draw y axis
    svg.append("g").call(yAxis);

    // Define colors
    const colors = ["lightblue", "LightSalmon", "Lavender", "lightyellow"];

    // Function to wrap text
    const wrapText = (text, width) => {
      text.each(function () {
        const text = d3.select(this);
        const words = text.text().split(/\s+/).reverse();
        let word;
        let line = [];
        let lineNumber = 0;
        const lineHeight = 1.1; // ems
        const x = text.attr("x");
        const y = text.attr("y");
        const dy = parseFloat(text.attr("dy")) || 0;
        let tspan = text
          .text(null)
          .append("tspan")
          .attr("x", x)
          .attr("y", y)
          .attr("dy", dy + "em");

        while ((word = words.pop())) {
          line.push(word);
          tspan.text(line.join(" "));
          if (tspan.node().getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(" "));
            line = [word];
            tspan = text
              .append("tspan")
              .attr("x", x)
              .attr("y", y)
              .attr("dy", ++lineNumber * lineHeight + dy + "em")
              .text(word);
          }
        }
      });
    };

    // Draw bars for each data point
    data.forEach((d, i) => {
      const barWidth = isMobile
        ? width - xScale(0)
        : xScale(parseDate(d.endDate)) - xScale(parseDate(d.startDate));
      const barHeight = isMobile
        ? yScale(parseDate(d.startDate)) - yScale(parseDate(d.endDate))
        : height - yScale(1);

      svg
        .append("rect")
        .attr("x", isMobile ? xScale(0) : xScale(parseDate(d.startDate)))
        .attr("y", isMobile ? yScale(parseDate(d.endDate)) : yScale(1)) // Top of the bar
        .attr("width", isMobile ? barWidth : barWidth)
        .attr("height", isMobile ? barHeight : barHeight) // Height of the bar
        .attr("fill", colors[i % colors.length]);

      // Add labels
      const label = svg
        .append("text")
        .attr(
          "x",
          isMobile ? xScale(0.5) : xScale(parseDate(d.startDate)) + barWidth / 2
        )
        .attr(
          "y",
          isMobile ? yScale(parseDate(d.endDate)) + barHeight / 2 : yScale(0.5)
        ) // Place label in the middle of the bar
        .attr("text-anchor", "middle")
        .attr("font-size", "18px")
        .attr("fill", "black")
        .text(d.label)
        .call(wrapText, isMobile ? width : barWidth);
    });
  }, [isMobile]); // Re-run effect when isMobile changes

  return <svg ref={svgRef}></svg>;
};

export default D3BarChart;
